import React, { useState, useEffect } from 'react';
import { Field, getIn, useFormikContext } from 'formik';
import { Button, Col, Form, FormControl, Row } from 'react-bootstrap';
import { useAbility } from '@casl/react';
import { useDispatch } from 'react-redux';
import memoizeOne from 'memoize-one';
import { camelCaseEmptyStringRecursive, sortByAttribute } from '../../services/utils';
import { activeContractsRequest } from '../../requests/contracts';
import { AbilityContext } from '../../config/abilityContext';
import {
  ButtonTooltip,
  ComponentDataTable,
  DatatableEmployeeName,
  EmployeeSearchModal,
  FormikSelect,
  Icon,
  SimpleCenteredModal
} from '../../components';

const columns = memoizeOne(handleClick => [
  {
    name: 'RUT Y NOMBRE',
    selector: 'employee.fullName',
    cell: item => <DatatableEmployeeName item={item.employee} fileName="fileInfo" name="fullName" fileUrl="fileUrl" />,
    grow: '1',
    sortable: true
  },
  {
    name: 'TURNO ACTUAL',
    selector: 'current_shift',
    cell: item => (item.lastShift ? <span>{item.lastShift.name}</span> : <span>Sin Turno</span>),
    grow: '1',
    sortable: true
  },
  {
    name: 'ACCIONES',
    cell: item => (
      <ButtonTooltip
        onClick={() => handleClick(item, 'destroy')}
        variant="circle-danger"
        className="btn-circle"
        size="sm"
        text="Eliminar"
      >
        <Icon icon="trash" />
      </ButtonTooltip>
    ),
    sortable: false,
    grow: '1',
    right: true
  }
]);

const selectorTypes = [{ label: 'Todos', value: 'all_contracts' }];

const ShiftAssignmentsEmployees = ({ shiftAssignment, shiftId, currentEmployee, rrhh = true }) => {
  const ability = useAbility(AbilityContext);
  const [filterEmployees, setFilterEmployees] = useState([]);
  const [selector, setSelector] = useState(selectorTypes);
  const [modalShow, setModalShow] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const { values, errors, setFieldValue, setFieldTouched, touched } = useFormikContext();
  const [loading, setLoading] = useState();
  const [placeholder, setPlaceholder] = useState('Seleccionar Trabajador');
  const dispatch = useDispatch();

  const successCallback = response => {
    setLoading(false);
    setPlaceholder('Seleccionar Trabajador');
    const { contractIds } = shiftAssignment;
    const contracts = camelCaseEmptyStringRecursive(response.data.data);
    const contractsToAdd = contracts.filter(selected => !contractIds.includes(selected.id));
    const vSelector = [...selectorTypes, ...contractsToAdd];
    setSelector(vSelector);
  };

  const fetchEmployees = () => {
    if (ability.can('read', 'Contract')) {
      setLoading(true);
      setPlaceholder('Cargando Trabajadores...');
      const params = { paginate: false };
      if (rrhh === false) {
        params.employees_belong_boss = currentEmployee;
      }
      activeContractsRequest({
        dispatch,
        params,
        successCallback
      });
    }
  };

  const removeFromTable = (contract, action) => {
    if (action !== 'destroy') return;
    const { contracts } = values.shiftAssignment;
    const notRemovedEmployees = contracts.filter(selected => selected.value !== contract.value);
    setSelector([...selector, contract]);
    setFieldValue('shiftAssignment[contracts]', notRemovedEmployees);
    if (notRemovedEmployees.length === 0) setFieldValue('shiftAssignment[contractIds]', ['']);
    else
      setFieldValue(
        'shiftAssignment[contractIds]',
        notRemovedEmployees.map(item => item.id)
      );
  };

  const RemoveAllFromTable = () => {
    const selectedRowsIds = selectedRows.map(item => item.id);
    const notRemovedEmployees = values.shiftAssignment.contracts.filter(
      selected => !selectedRowsIds.includes(selected.id)
    );
    setSelector([...selectorTypes, ...selector, ...selectedRows].filter(selected => selected.label));
    setFieldValue('shiftAssignment[contracts]', notRemovedEmployees);
    setFieldValue(
      'shiftAssignment[contractIds]',
      notRemovedEmployees.map(item => item.id)
    );

    if (notRemovedEmployees.length === 0) setFieldValue('shiftAssignment[contractIds]', ['']);
    setFilterEmployees(notRemovedEmployees);
    setSelectedCount(0);
    setSelectedRows([]);
  };

  const filterArray = (array, query, keys) => {
    if (query === '') return array;
    if (array.length === 0) return array;
    return array.filter(item => {
      let inQuery = false;
      keys.forEach(key => {
        if (item.employee[[key]]?.toLowerCase().includes(query.toLowerCase())) inQuery = true;
      });
      return inQuery;
    });
  };

  const filteredArray = () => {
    setFilterEmployees(
      filterArray(values.shiftAssignment.contracts, textInput, ['fullName', 'nationalIdentification'])
    );
  };

  const addAllEmployees = () => {
    const { shiftAssignment: shiftAssignmentValues } = values;
    const vSelector = selector.filter(selected => selected.id !== undefined);
    const selectedEmployeeIds = vSelector.map(employee => employee.value);
    const selectedEmployees = vSelector.map(employee => ({ ...employee }));
    setSelector([selectorTypes]);
    setFieldValue('employeeSelector', '');
    setFieldValue('shiftAssignment[contracts]', [...selectedEmployees, ...shiftAssignmentValues.contracts]);
    setFilterEmployees([...selectedEmployees, ...shiftAssignmentValues.contracts]);
    setFieldValue('shiftAssignment[contractIds]', [...selectedEmployeeIds, ...shiftAssignmentValues.contractIds]);
  };

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const handleSelector = () => {
    const vSelector = selector.filter(selected => selected.value !== values.employeeSelector);
    if (vSelector.length === selector.length) return;
    if (values.employeeSelector === 'all_contracts') {
      addAllEmployees();
    } else {
      const employeeToAdd = selector.find(selected => selected.value === values.employeeSelector);
      setFieldValue(`shiftAssignment[contractIds]`, [...values.shiftAssignment.contractIds, employeeToAdd.value]);
      setFieldValue('shiftAssignment[contracts]', [...values.shiftAssignment.contracts, employeeToAdd]);
      setFilterEmployees([...values.shiftAssignment.contracts, employeeToAdd]);
      setSelector(vSelector);
    }
  };

  const handleSearch = selectedEmployees => {
    setModalShow(false);
    const vSelector = selector.filter(selected => !selectedEmployees.includes(selected.employee?.id));
    if (vSelector.length === selector.length) return;
    const contractsToAdd = selector.filter(selected => selectedEmployees.includes(selected.employee?.id));
    setFieldValue(`shiftAssignment[contractIds]`, [
      ...values.shiftAssignment.contractIds,
      ...contractsToAdd.map(item => item.value)
    ]);
    setFieldValue('shiftAssignment[contracts]', [...values.shiftAssignment.contracts, ...contractsToAdd]);
    setSelector(vSelector);
  };

  const subHeaderComponent = () => (
    <>
      <ButtonTooltip
        onClick={RemoveAllFromTable}
        variant="circle-danger"
        className="btn-circle"
        size="sm"
        text="Eliminar Seleccionados"
        disabled={selectedCount === 0}
      >
        <Icon icon="trash" />
      </ButtonTooltip>
      <Form.Group className="ml-auto w-25">
        <FormControl
          placeholder="Buscar"
          name="textInput"
          value={textInput}
          onChange={e => setTextInput(e.target.value)}
        />
      </Form.Group>
    </>
  );

  useEffect(fetchEmployees, [shiftAssignment, shiftId]);
  useEffect(filteredArray, [textInput, values.shiftAssignment.contracts]);

  return (
    <>
      <div className="info-box ml-0 mt-2">
        <h4 className="text-uppercase">Selecciona Trabajadores</h4>
        <p className="info">
          <span className="full-width">Seleccione trabajadores a los que se le asignará este turno</span>
        </p>
      </div>
      <Row>
        <Col md={5}>
          <Field name="employeeSelector">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label=""
                placeholder={placeholder}
                isDisabled={loading}
                options={sortByAttribute(selector, 'fullName')}
                defaultValue="all_employees"
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs={6} md={2} xl={1}>
          <Button variant="primary" onClick={handleSelector} disabled={loading}>
            Agregar
          </Button>
        </Col>
        {!loading && (
          <Col className="ml-2">
            <ButtonTooltip
              variant="circle-primary"
              className="advance-search"
              toolbarVariant="mt-n3"
              text="Búsqueda avanzada de empleados mediante filtros de cargos, Lugares de Prestación de Servicios, otros"
              onClick={() => setModalShow(true)}
            >
              <Icon className="w-100 h-100" icon="people-circle" />
            </ButtonTooltip>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={12}>
          <ComponentDataTable
            columns={columns(removeFromTable)}
            data={filterEmployees}
            onRequest={false}
            totalRows={filterEmployees.length}
            defaultSortField="fullName"
            pointerOnHover
            sortServer={false}
            subHeaderComponent={subHeaderComponent()}
            onSelectedRowsChange={handleSelectedRows}
            selectableRows
            paginationServer={false}
            pagination
          />
        </Col>
      </Row>
      <SimpleCenteredModal
        title="Buscar Empleados"
        body={
          <EmployeeSearchModal
            customParams={{ active: true, shiftId }}
            handleClose={() => setModalShow(false)}
            formRequest={handleSearch}
          />
        }
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </>
  );
};
export default ShiftAssignmentsEmployees;
